/**
 * Per-game offline submit queue for sibling games.
 *
 * Drift has its own queue at `game/api.ts::aether.queue` (predates
 * this module). Each per-game key here is `aether.queue.<slug>` so
 * the per-game queues are isolated and a Drift retry can't get
 * dispatched against a sibling-game endpoint.
 *
 * Sibling games call:
 *  - `enqueueFailedSubmit("<slug>", url, body)` from their
 *    `finish<Game>Run()` catch branch (and on transient 5xx).
 *  - `flushFailedSubmits("<slug>")` once at startup so previously
 *    queued submits retry on the next page load.
 *
 * Failure semantics:
 *  - 4xx responses are NOT enqueued (request is invalid; retry
 *    won't help — bad handle, bad token, rate limit, etc).
 *  - 5xx and network errors ARE enqueued (transient — D1 hiccup,
 *    edge failover, connection drop).
 *  - Queue capped at 20 entries per game to bound localStorage
 *    usage; oldest entries are dropped first.
 *
 * Privacy: the queued body contains whatever the per-game submit
 * carries (handle + events). It lives in the user's own
 * localStorage and never leaves the device until the retry POSTs
 * it to the same /api/runs/<slug>/<run_id> endpoint it was
 * destined for originally.
 */

interface QueuedSubmit {
  url: string;
  body: unknown;
  ts: number;
}

const KEY_PREFIX = "aether.queue.";
const MAX_QUEUE = 20;

function key(game: string): string {
  return KEY_PREFIX + game;
}

function load(game: string): QueuedSubmit[] {
  try {
    const raw = localStorage.getItem(key(game));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(game: string, q: QueuedSubmit[]): void {
  try {
    localStorage.setItem(key(game), JSON.stringify(q));
  } catch {
    /* localStorage full or disabled — swallow */
  }
}

export function enqueueFailedSubmit(game: string, url: string, body: unknown): void {
  const q = load(game);
  if (q.length >= MAX_QUEUE) q.shift(); // FIFO drop oldest
  q.push({ url, body, ts: Date.now() });
  save(game, q);
}

export async function flushFailedSubmits(game: string): Promise<void> {
  const q = load(game);
  if (!q.length) return;
  const remaining: QueuedSubmit[] = [];
  for (const item of q) {
    try {
      const res = await fetch(item.url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(item.body),
      });
      // Keep in queue only on transient failure; 4xx = drop (invalid).
      if (!res.ok && res.status >= 500) {
        remaining.push(item);
      }
    } catch {
      remaining.push(item);
    }
  }
  save(game, remaining);
}

/** True if the response/error class warrants enqueueing for retry. */
export function isRetryable(status: number): boolean {
  return status === 0 || status >= 500;
}
